import { parseLapTimeSeconds } from './lapTime';

/**
 * Sector times per lap, kept client-side.
 *
 * Apex only ever shows the *current* S1/S2/S3 cells, and the backend forwards them
 * as they are, so there is no per-lap record on the wire. They are recovered here:
 * when a team's lap counter ticks over, its cells still hold the lap just finished
 * (the next S1 arrives a moment later), so that is the instant to take them.
 *
 * A lap is kept only when its three sectors add up to the lap time. That is what
 * leaves out pit in-laps (the stop is inside S3, which then reads `"2:29.9"`) and
 * any lap whose cells had already started refreshing.
 */

export type SectorIndex = 0 | 1 | 2;

export interface SectorLap {
  /** Position within the stint, as `lapHistory` addresses laps. */
  stintIndex: number;
  lapIndex: number;
  sectors: [number, number, number];
  lapSeconds: number;
  /** Epoch ms the lap was seen (live) or happened (replay, backfill). */
  at: number;
}

interface TeamSectors {
  laps: SectorLap[];
  /** `stint:lap` addresses already stored, so a repeated frame adds nothing. */
  keys: Set<string>;
  /** What the previous frame said, to notice the counter moving. */
  lastSeen: { lapCount: number; stint: string } | null;
}

export type SectorHistory = Map<string, TeamSectors>;

/** The parts of a team update this needs; both `TeamData` and `TeamInfo` fit. */
export interface SectorFrame {
  id: string;
  sector1: string;
  sector2: string;
  sector3: string;
  lastLap: string;
  pitCount: string;
  inBox: boolean;
  lapCount: number;
}

/** How far the sectors may stray from the lap time and still count as one lap. */
export const SECTOR_SUM_TOLERANCE = 0.05;

export function emptySectorHistory(): SectorHistory {
  return new Map();
}

/** A sector cell in seconds, or null for blanks and placeholders. */
export function parseSector(text: string): number | null {
  const seconds = parseLapTimeSeconds((text ?? '').trim());
  return seconds !== null && seconds > 0 ? seconds : null;
}

function teamEntry(history: SectorHistory, teamId: string): TeamSectors {
  let entry = history.get(teamId);
  if (!entry) {
    entry = { laps: [], keys: new Set(), lastSeen: null };
    history.set(teamId, entry);
  }
  return entry;
}

function stintOf(pitCount: string): number {
  const n = parseInt(pitCount, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * Take the finished lap's sectors from a team frame, if it carries one.
 * Returns true when a lap was stored, so callers know when to refresh.
 *
 * `lapCount` is the backend's count of laps it has recorded in the stint; it only
 * moves for laps it kept, which already excludes the out-lap after a stop.
 */
export function recordSectors(
  history: SectorHistory,
  frame: SectorFrame,
  at: number,
): boolean {
  const entry = teamEntry(history, frame.id);
  const previous = entry.lastSeen;
  entry.lastSeen = { lapCount: frame.lapCount, stint: frame.pitCount };

  // Nothing to compare against on the first sighting, and a counter that did not
  // advance means the lap is the same one seen again.
  if (!previous || previous.stint !== frame.pitCount) return false;
  if (frame.lapCount <= previous.lapCount) return false;
  if (frame.inBox) return false;

  const lap = parseLapTimeSeconds(frame.lastLap);
  const s1 = parseSector(frame.sector1);
  const s2 = parseSector(frame.sector2);
  const s3 = parseSector(frame.sector3);
  if (lap === null || s1 === null || s2 === null || s3 === null) return false;
  if (Math.abs(s1 + s2 + s3 - lap) > SECTOR_SUM_TOLERANCE) return false;

  const stintIndex = stintOf(frame.pitCount);
  const lapIndex = frame.lapCount - 1;
  const key = `${stintIndex}:${lapIndex}`;
  if (entry.keys.has(key)) return false;

  entry.keys.add(key);
  entry.laps.push({ stintIndex, lapIndex, sectors: [s1, s2, s3], lapSeconds: lap, at });
  return true;
}

/** A lap row as stored by the backend (`GET /events/:id/laps`). */
export interface StoredLapRow {
  teamId: string;
  lapTime: string;
  sector1: string;
  sector2: string;
  sector3: string;
  recordedAt: string;
}

const BACKFILL_MATCH_MS = 30_000;

/**
 * Add laps the page missed because it joined late. Rows that were also seen live
 * are skipped (same lap and sector times, close in time); what remains goes in
 * front, since it all happened earlier. Returns how many laps were added.
 */
export function mergeStoredLaps(history: SectorHistory, rows: StoredLapRow[]): number {
  const byTeam = new Map<string, StoredLapRow[]>();
  for (const row of rows) {
    const list = byTeam.get(row.teamId);
    if (list) list.push(row);
    else byTeam.set(row.teamId, [row]);
  }

  let added = 0;
  byTeam.forEach((teamRows, teamId) => {
    const entry = teamEntry(history, teamId);
    const fresh: SectorLap[] = [];

    for (const row of teamRows) {
      const lap = parseLapTimeSeconds(row.lapTime);
      const s1 = parseSector(row.sector1);
      const s2 = parseSector(row.sector2);
      const s3 = parseSector(row.sector3);
      if (lap === null || s1 === null || s2 === null || s3 === null) continue;
      if (Math.abs(s1 + s2 + s3 - lap) > SECTOR_SUM_TOLERANCE) continue;

      const at = Date.parse(row.recordedAt);
      if (!Number.isFinite(at)) continue;

      const seenLive = entry.laps.some(
        (l) =>
          Math.abs(l.lapSeconds - lap) < 0.0005 &&
          l.sectors[0] === s1 &&
          l.sectors[1] === s2 &&
          l.sectors[2] === s3 &&
          Math.abs(l.at - at) <= BACKFILL_MATCH_MS,
      );
      if (seenLive) continue;

      // Stored laps are addressed apart from live ones: the stint is not stored.
      fresh.push({ stintIndex: -1, lapIndex: fresh.length, sectors: [s1, s2, s3], lapSeconds: lap, at });
    }

    if (fresh.length === 0) return;
    fresh.sort((a, b) => a.at - b.at);
    entry.laps = [...fresh, ...entry.laps];
    added += fresh.length;
  });
  return added;
}

// ── Reading ──────────────────────────────────────────────────────────────────

export function teamSectorLaps(history: SectorHistory, teamId: string): SectorLap[] {
  return history.get(teamId)?.laps ?? [];
}

export interface SectorStats {
  laps: number;
  /** Fastest of each sector, over all kept laps. */
  best: [number, number, number] | null;
  /** Mean of each sector over the last N kept laps (all of them when N is 0). */
  average: [number, number, number] | null;
  last: [number, number, number] | null;
  /** Best S1 + best S2 + best S3: the lap the team could do if it put them together. */
  theoreticalBest: number | null;
}

function sum3(values: [number, number, number]): number {
  return values[0] + values[1] + values[2];
}

export function sectorStats(laps: SectorLap[], lastN = 0): SectorStats {
  if (laps.length === 0) {
    return { laps: 0, best: null, average: null, last: null, theoreticalBest: null };
  }

  const best: [number, number, number] = [Infinity, Infinity, Infinity];
  for (const lap of laps) {
    for (let i = 0; i < 3; i += 1) best[i] = Math.min(best[i], lap.sectors[i]);
  }

  const window = lastN > 0 ? laps.slice(-lastN) : laps;
  const average: [number, number, number] = [0, 0, 0];
  for (const lap of window) {
    for (let i = 0; i < 3; i += 1) average[i] += lap.sectors[i];
  }
  for (let i = 0; i < 3; i += 1) average[i] /= window.length;

  return {
    laps: laps.length,
    best,
    average,
    last: laps[laps.length - 1].sectors,
    theoreticalBest: sum3(best),
  };
}

/** The fastest value of each sector across every team given. */
export function fieldBest(stats: SectorStats[]): [number, number, number] | null {
  let out: [number, number, number] | null = null;
  for (const s of stats) {
    if (!s.best) continue;
    out = out
      ? [Math.min(out[0], s.best[0]), Math.min(out[1], s.best[1]), Math.min(out[2], s.best[2])]
      : [...s.best];
  }
  return out;
}

export interface SectorDelta {
  /** Other minus reference per sector: positive means the other team is slower. */
  best: [number, number, number] | null;
  average: [number, number, number] | null;
}

function diff(
  a: [number, number, number] | null,
  b: [number, number, number] | null,
): [number, number, number] | null {
  return a && b ? [a[0] - b[0], a[1] - b[1], a[2] - b[2]] : null;
}

export function compareSectors(reference: SectorStats, other: SectorStats): SectorDelta {
  return {
    best: diff(other.best, reference.best),
    average: diff(other.average, reference.average),
  };
}

/** One sector's time per kept lap, oldest first. */
export function sectorSeries(laps: SectorLap[], sector: SectorIndex): number[] {
  return laps.map((lap) => lap.sectors[sector]);
}
